import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as authenticationService from '../../data-layer/authentication/service';
import {
    addNewCollectionIdentifiers,
    setCollectionsRecords,
} from '../../data-layer/collections/actions';
import * as collectionsService from '../../data-layer/collections/service';
import { DEFAULT_COLLECTION_ID } from '../../data-layer/collections/service';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { getPostSuggests } from '../../data-layer/posts/service';
import { useDispatch } from '../../data-layer/useDispatch';
import { useCpaTrial } from '../../ui/users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { SaveToCollectionDrawer } from '../../ui/users/SaveToCollectionDrawer/SaveToCollectionDrawer';
import { BookmarkStep } from '../../ui/users/SaveToCollectionDrawer/types';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { useEventSource } from '../../utils/LocationContext';
import { EventSource } from '../../utils/analyticsService';
import { getWindow } from '../../utils/getWindow';
import { useBackButton } from '../../utils/useBackButton';
import { useSwitchDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCollectionInfo } from './hooks/useCollectionInfo';
import { useCollectionsController } from './useCollectionsController';

export const SaveToCollectionDialog = () => {
    const { state, pathname } = useLocation();
    const postId = state?.postId;
    const dispatch = useDispatch();
    const onNavigateBack = useBackButton();
    const controller = useCollectionsController();
    const switchDialog = useSwitchDialog(true);
    const { eventSource } = useEventSource();
    const { isNotRegistered, refreshUserProfile } = useCpaTrial();

    const screen = eventSource ?? EventSource.POST;

    const [step, setStep] = useState<BookmarkStep>(BookmarkStep.ADD_TO_EXIST_COLLECTION);

    const onAddClick = useCallback(() => {
        contentActionsService.trackCollectionCreateDialogOpen({
            id: postId,
            screen,
        });
        setStep(BookmarkStep.ADD_TO_NEW_COLLECTION);
    }, [postId, screen]);

    const { values, handlers } = useCollectionInfo({
        initialValues: {
            numberOfPosts: 1,
            displayName: '',
            isPublic: true,
        },
        id: postId,
        getSuggestMethod: () => getPostSuggests(postId, screen),
        needLoadSuggests: step === BookmarkStep.ADD_TO_NEW_COLLECTION,
        screen,
        mode: 'create',
    });

    const onSaveToExistentCollection = authenticationService.protect(
        async (collectionId: string) => {
            const collectionModel = controller.createdCollections.find(
                (collection) => collection.id === collectionId,
            );

            const promoShowed = getWindow()?.localStorage.getItem(
                `${collectionId}_promo_feed_showed`,
            );

            const needShowPromotionDialog = Boolean(
                collectionModel?.numberOfPosts &&
                    collectionModel?.numberOfPosts >= 3 &&
                    !collectionModel.feedStatus?.showInFeed &&
                    values.canShowInFeed &&
                    !promoShowed,
            );

            try {
                const response = await collectionsService.saveToExistentCollection(
                    collectionId,
                    postId,
                );
                dispatch(setCollectionsRecords(...response.records.collections));

                contentActionsService.trackAddPostToCollection({
                    postId,
                    collectionId,
                    screen,
                });

                if (needShowPromotionDialog) {
                    getWindow()?.localStorage.setItem(`${collectionId}_promo_feed_showed`, 'true');
                    switchDialog(DIALOGS.PROMOTE_SHOW_IN_FEED_COLLECTION_DIALOG_NAME, {
                        collectionId,
                    });

                    return;
                }
            } catch (error) {
                dispatch(showNotificationAsync('Не удалось сохранить пост в коллекцию'));
            }

            onNavigateBack();
        },
    );

    const onSaveToNewCollection = authenticationService.protect(async () => {
        try {
            const response = await collectionsService.saveToNewCollection({
                postIds: [postId],
                collectionName: values.showInFeed ? values.currentSuggest : values.collectionName,
                isPublic: values.isPublic,
                showInFeed: values.showInFeed,
                needCpaRegistration: isNotRegistered,
            });
            dispatch(setCollectionsRecords(...response.records.collections));
            dispatch(
                addNewCollectionIdentifiers({
                    namespace: 'default',
                    identifiers: response.collections,
                }),
            );
            contentActionsService.trackCollectionCreated({
                id: response.collections[0],
                postIds: [postId],
                screen,
                newValues: {
                    displayName: response.records.collections[0].displayName,
                    showInFeed: response.records.collections[0].feedStatus?.showInFeed,
                    isPublic: response.records.collections[0].isPublic,
                },
            });
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось сохранить пост в коллекцию'));
        }

        if (isNotRegistered) {
            refreshUserProfile();
        }

        onNavigateBack();
    });

    const onClose = useCallback(() => {
        contentActionsService.trackSaveToCollectionDialogClose({
            id: postId,
            screen,
        });
        onNavigateBack();
    }, [onNavigateBack, postId, screen]);

    if (!postId) {
        return null;
    }

    return (
        <SaveToCollectionDrawer
            isCollectionsLoading={controller.isLoading}
            collections={controller.createdCollections.filter(
                (collection) => collection.id !== DEFAULT_COLLECTION_ID,
            )}
            onSaveToExistentCollection={onSaveToExistentCollection}
            onClose={onClose}
            values={values}
            handlers={{ ...handlers, onSubmit: onSaveToNewCollection }}
            step={step}
            onAddClick={onAddClick}
            isNotRegistered={isNotRegistered}
        />
    );
};
